import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Row, Col, Modal, Spinner } from 'react-bootstrap'
import { motion, AnimatePresence } from 'framer-motion'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import {
  FaUser, FaPlus, FaEdit, FaTrash, FaPhone,
  FaIdCard, FaCalendarAlt, FaSearch, FaCheckCircle, FaTimesCircle,
  FaMapMarkerAlt, FaUpload,
} from 'react-icons/fa'
import {
  fetchDrivers, addDriver, updateDriver, deleteDriver, clearSelectedDriver,
} from '../../store/slices/driverSlice'
import '../../assets/scss/pages/DriverManagement.scss'

const fade = {
  hidden  : { opacity: 0, y: 12 },
  visible : i => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.32, ease: 'easeOut' } }),
}

const validationSchema = Yup.object({
  name               : Yup.string().trim().required('Driver name is required'),
  mobile             : Yup.string().matches(/^\d{10}$/, 'Enter a valid 10-digit mobile number').required('Mobile number is required'),
  address            : Yup.string().trim().required('Address is required'),
  aadharNumber       : Yup.string().matches(/^\d{12}$/, 'Enter a valid 12-digit Aadhaar number').required('Aadhaar number is required'),
  drivingLicenseNumber: Yup.string().trim().required('License number is required'),
  licenseValidUpto   : Yup.string().required('License validity date is required'),
  email              : Yup.string().email('Enter a valid email').nullable(),
  password           : Yup.string().min(6, 'Minimum 6 characters').nullable(),
})

export default function DriverManagement() {
  const dispatch   = useDispatch()
  const { drivers = [], loading } = useSelector(s => s.driver)

  const [showModal,      setShowModal]      = useState(false)
  const [editingDriver,  setEditingDriver]  = useState(null)
  const [deleteModal,    setDeleteModal]    = useState(false)
  const [driverToDelete, setDriverToDelete] = useState(null)
  const [statusFilter,   setStatusFilter]   = useState('all')
  const [search,         setSearch]         = useState('')

  useEffect(() => { dispatch(fetchDrivers()) }, [dispatch])

  const filtered = drivers.filter(d => {
    const q = search.toLowerCase()
    const matchSearch =
      d.name?.toLowerCase().includes(q) ||
      d.mobile?.toLowerCase().includes(q) ||
      d.drivingLicenseNumber?.toLowerCase().includes(q)
    const status = d.isActive !== false ? 'active' : 'inactive'
    const matchStatus = statusFilter === 'all' || statusFilter === status
    return matchSearch && matchStatus
  })

  const totalDrivers    = drivers.length
  const activeDrivers   = drivers.filter(d => d.isActive !== false).length
  const inactiveDrivers = drivers.filter(d => d.isActive === false).length

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name                : editingDriver?.name                 ?? '',
      mobile              : editingDriver?.mobile               ?? '',
      address             : editingDriver?.address              ?? '',
      aadharNumber        : editingDriver?.aadharNumber         ?? '',
      drivingLicenseNumber: editingDriver?.drivingLicenseNumber ?? '',
      licenseValidUpto    : editingDriver?.licenseValidUpto ? editingDriver.licenseValidUpto.split('T')[0] : '',
      email               : editingDriver?.email                ?? '',
      password            : '',
      fileAadhar          : null,
      fileLicense         : null,
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      const payload = new FormData()
      Object.entries(values).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== '') payload.append(k, v)
      })
      try {
        if (editingDriver) {
          await dispatch(updateDriver({ id: editingDriver._id, data: payload })).unwrap()
        } else {
          await dispatch(addDriver(payload)).unwrap()
        }
        resetForm()
        setShowModal(false)
        setEditingDriver(null)
        dispatch(fetchDrivers())
      } catch { /* errors handled by slice */ }
      finally { setSubmitting(false) }
    },
  })

  const openAdd    = () => { setEditingDriver(null); dispatch(clearSelectedDriver()); formik.resetForm(); setShowModal(true) }
  const openEdit   = d  => { setEditingDriver(d); setShowModal(true) }
  const closeModal = () => { setShowModal(false); setEditingDriver(null); formik.resetForm() }
  const confirmDelete = d => { setDriverToDelete(d); setDeleteModal(true) }
  const handleDelete  = async () => {
    if (!driverToDelete) return
    await dispatch(deleteDriver(driverToDelete._id)).unwrap()
    setDeleteModal(false)
    setDriverToDelete(null)
    dispatch(fetchDrivers())
  }

  const fld = key => ({
    name     : key,
    value    : formik.values[key],
    onChange : formik.handleChange,
    onBlur   : formik.handleBlur,
    className: `dm-input${formik.touched[key] && formik.errors[key] ? ' dm-input--error' : ''}`,
  })

  const tabs = [
    { key: 'all',      label: 'All',      count: totalDrivers },
    { key: 'active',   label: 'Active',   count: activeDrivers },
    { key: 'inactive', label: 'Inactive', count: inactiveDrivers },
  ]

  return (
    <div className="dm-page">

      {/* Header */}
      <motion.div initial="hidden" animate="visible" variants={fade} custom={0} className="dm-header">
        <div className="dm-header-left">
          <div className="dm-header-icon"><FaUser /></div>
          <div>
            <h4 className="dm-title">Driver Management</h4>
            <p className="dm-sub">Manage and track all registered drivers</p>
          </div>
        </div>
        <button className="dm-add-btn" onClick={openAdd}>
          <FaPlus style={{ fontSize: '0.72rem' }} /> Add Driver
        </button>
      </motion.div>

      {/* KPI Cards */}
      <Row className="g-3 mb-3">
        {[
          { label: 'Total Drivers',    val: totalDrivers,    mod: 'blue',  icon: <FaUser />,        note: 'Registered drivers'   },
          { label: 'Active Drivers',   val: activeDrivers,   mod: 'green', icon: <FaCheckCircle />, note: 'Currently active'     },
          { label: 'Inactive Drivers', val: inactiveDrivers, mod: 'red',   icon: <FaTimesCircle />, note: 'Not currently active' },
        ].map((k, i) => (
          <Col xs={12} md={4} key={k.label}>
            <motion.div initial="hidden" animate="visible" variants={fade} custom={i + 1} className={`dm-kpi dm-kpi--${k.mod}`}>
              <div className="dm-kpi-left">
                <span className="dm-kpi-val">{k.val}</span>
                <span className="dm-kpi-label">{k.label}</span>
                <span className="dm-kpi-note">{k.note}</span>
              </div>
              <span className="dm-kpi-icon">{k.icon}</span>
            </motion.div>
          </Col>
        ))}
      </Row>

      {/* Filter Bar */}
      <motion.div initial="hidden" animate="visible" variants={fade} custom={4} className="dm-filter-bar">
        <div className="dm-search-wrap">
          <FaSearch className="dm-search-icon" />
          <input className="dm-search" placeholder="Search drivers…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="dm-filter-tabs">
          {tabs.map(t => (
            <button key={t.key} className={`dm-tab${statusFilter === t.key ? ' dm-tab--active' : ''}`} onClick={() => setStatusFilter(t.key)}>
              {t.label}
            </button>
          ))}
        </div>
        <span className="dm-count">{filtered.length} driver{filtered.length !== 1 ? 's' : ''}</span>
      </motion.div>

      {/* Driver List */}
      {loading ? (
        <div className="dm-loading">
          <Spinner animation="border" size="sm" style={{ color: 'rgb(20,35,115)' }} />
          <span>Loading drivers…</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="dm-empty">
          <div className="dm-empty-icon"><FaUser /></div>
          <p className="dm-empty-title">{search || statusFilter !== 'all' ? 'No matching drivers' : 'No drivers yet'}</p>
          <p className="dm-empty-sub">{search || statusFilter !== 'all' ? 'Try adjusting your search or filters.' : 'Add your first driver to get started.'}</p>
        </div>
      ) : (
        <Row className="g-3">
          <AnimatePresence>
            {filtered.map((driver, i) => {
              const isActive     = driver.isActive !== false
              const initials     = driver.name ? driver.name.trim().charAt(0).toUpperCase() : 'D'
              const licExpiry    = driver.licenseValidUpto
                ? new Date(driver.licenseValidUpto).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                : '—'
              const aadharMasked = driver.aadharNumber
                ? `XXXX XXXX ${driver.aadharNumber.slice(-4)}`
                : '—'

              return (
                <Col xs={12} sm={6} md={4} xl={3} key={driver._id}>
                  <motion.div
                    className="dm-card"
                    initial="hidden" animate="visible" exit={{ opacity: 0, y: -8 }}
                    variants={fade} custom={i}
                    layout
                  >
                    <div className="dm-card-accent" />
                    <div className="dm-card-head">
                      <div className="dm-card-avatar">{initials}</div>
                      <div className="dm-card-meta">
                        <span className="dm-card-name">{driver.name}</span>
                        <span className={`dm-badge dm-badge--${isActive ? 'active' : 'inactive'}`}>
                          {isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                    <div className="dm-card-details">
                      <div className="dm-card-row">
                        <FaIdCard className="dm-card-icon" />
                        <span className="dm-card-mono">{driver.drivingLicenseNumber || '—'}</span>
                      </div>
                      <div className="dm-card-row">
                        <FaPhone className="dm-card-icon" />
                        <span className="dm-card-val">{driver.mobile || '—'}</span>
                      </div>
                      <div className="dm-card-row">
                        <FaCalendarAlt className="dm-card-icon" />
                        <span className="dm-card-val">Valid: {licExpiry}</span>
                      </div>
                      <div className="dm-card-row">
                        <FaIdCard className="dm-card-icon" />
                        <span className="dm-card-val">{aadharMasked}</span>
                      </div>
                      {driver.address && (
                        <div className="dm-card-row">
                          <FaMapMarkerAlt className="dm-card-icon" />
                          <span className="dm-card-val" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{driver.address}</span>
                        </div>
                      )}
                    </div>
                    <div className="dm-card-footer">
                      <button className="dm-card-btn dm-card-btn--edit" onClick={() => openEdit(driver)}>
                        <FaEdit style={{ fontSize: '0.68rem' }} /> Edit
                      </button>
                      <button className="dm-card-btn dm-card-btn--delete" onClick={() => confirmDelete(driver)}>
                        <FaTrash style={{ fontSize: '0.63rem' }} />
                      </button>
                    </div>
                  </motion.div>
                </Col>
              )
            })}
          </AnimatePresence>
        </Row>
      )}

      {/* Add / Edit Modal */}
      <Modal show={showModal} onHide={closeModal} centered size="lg" backdrop="static">
        <form onSubmit={formik.handleSubmit}>
          <Modal.Header className="dm-modal-hero" as="div">
            <button type="button" className="dm-modal-close" onClick={closeModal} aria-label="Close">✕</button>
            <div className="dm-modal-hero-icon">
              {editingDriver ? <FaEdit /> : <FaUser />}
            </div>
            <div className="dm-modal-hero-text">
              <h2 className="dm-modal-hero-title">{editingDriver ? 'Edit Driver' : 'Add New Driver'}</h2>
              <p className="dm-modal-hero-sub">
                {editingDriver ? `Updating record for ${editingDriver.name}` : 'Enter driver details, license and document information'}
              </p>
            </div>
          </Modal.Header>
          <Modal.Body className="dm-modal-body">
            <Row className="g-3">
              <Col xs={12} md={6}>
                <div className="dm-form-group">
                  <label className="dm-label">
                    <FaUser className="dm-lbl-icon" /> Name <span className="dm-required">*</span>
                  </label>
                  <input {...fld('name')} placeholder="Full name" />
                  {formik.touched.name && formik.errors.name && <span className="dm-error">{formik.errors.name}</span>}
                </div>
              </Col>
              <Col xs={12} md={6}>
                <div className="dm-form-group">
                  <label className="dm-label">
                    <FaPhone className="dm-lbl-icon" /> Mobile <span className="dm-required">*</span>
                  </label>
                  <input {...fld('mobile')} placeholder="10-digit mobile number" maxLength={10} />
                  {formik.touched.mobile && formik.errors.mobile && <span className="dm-error">{formik.errors.mobile}</span>}
                </div>
              </Col>
              <Col xs={12} md={6}>
                <div className="dm-form-group">
                  <label className="dm-label">
                    <FaIdCard className="dm-lbl-icon" /> Driving License No. <span className="dm-required">*</span>
                  </label>
                  <input {...fld('drivingLicenseNumber')} placeholder="e.g. MH01 2024 0001234" />
                  {formik.touched.drivingLicenseNumber && formik.errors.drivingLicenseNumber && <span className="dm-error">{formik.errors.drivingLicenseNumber}</span>}
                </div>
              </Col>
              <Col xs={12} md={6}>
                <div className="dm-form-group">
                  <label className="dm-label">
                    <FaCalendarAlt className="dm-lbl-icon" /> License Valid Upto <span className="dm-required">*</span>
                  </label>
                  <input type="date" {...fld('licenseValidUpto')} />
                  {formik.touched.licenseValidUpto && formik.errors.licenseValidUpto && <span className="dm-error">{formik.errors.licenseValidUpto}</span>}
                </div>
              </Col>
              <Col xs={12} md={6}>
                <div className="dm-form-group">
                  <label className="dm-label">
                    <FaIdCard className="dm-lbl-icon" /> Aadhaar Number <span className="dm-required">*</span>
                  </label>
                  <input {...fld('aadharNumber')} placeholder="12-digit Aadhaar number" maxLength={12} />
                  {formik.touched.aadharNumber && formik.errors.aadharNumber && <span className="dm-error">{formik.errors.aadharNumber}</span>}
                </div>
              </Col>
              <Col xs={12} md={6}>
                <div className="dm-form-group">
                  <label className="dm-label">
                    <FaMapMarkerAlt className="dm-lbl-icon" /> Address <span className="dm-required">*</span>
                  </label>
                  <input {...fld('address')} placeholder="Full address" />
                  {formik.touched.address && formik.errors.address && <span className="dm-error">{formik.errors.address}</span>}
                </div>
              </Col>
              <Col xs={12} md={6}>
                <div className="dm-form-group">
                  <label className="dm-label">Email <span style={{ fontWeight: 400, opacity: 0.7, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                  <input type="email" {...fld('email')} placeholder="driver@example.com" />
                  {formik.touched.email && formik.errors.email && <span className="dm-error">{formik.errors.email}</span>}
                </div>
              </Col>
              <Col xs={12} md={6}>
                <div className="dm-form-group">
                  <label className="dm-label">Password <span style={{ fontWeight: 400, opacity: 0.7, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                  <input type="password" {...fld('password')} placeholder="Min 6 characters" />
                  {formik.touched.password && formik.errors.password && <span className="dm-error">{formik.errors.password}</span>}
                </div>
              </Col>
              <Col xs={12} md={6}>
                <div className="dm-form-group">
                  <label className="dm-label"><FaIdCard className="dm-lbl-icon" /> Aadhaar Document</label>
                  <label className="dm-file-label">
                    <FaUpload className="dm-file-icon" />
                    <span className="dm-file-text">Choose File</span>
                    <input
                      type="file" accept="image/*,application/pdf" className="dm-file-hidden"
                      onChange={e => formik.setFieldValue('fileAadhar', e.currentTarget.files[0])}
                    />
                  </label>
                </div>
              </Col>
              <Col xs={12} md={6}>
                <div className="dm-form-group">
                  <label className="dm-label"><FaIdCard className="dm-lbl-icon" /> License Document</label>
                  <label className="dm-file-label">
                    <FaUpload className="dm-file-icon" />
                    <span className="dm-file-text">Choose File</span>
                    <input
                      type="file" accept="image/*,application/pdf" className="dm-file-hidden"
                      onChange={e => formik.setFieldValue('fileLicense', e.currentTarget.files[0])}
                    />
                  </label>
                </div>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="dm-modal-footer">
            <div className="dm-modal-footer-note"><span className="dm-required">*</span> Required field</div>
            <div className="dm-modal-footer-actions">
              <button type="button" className="dm-btn-cancel" onClick={closeModal} disabled={formik.isSubmitting}>Cancel</button>
              <button type="submit" className="dm-btn-submit" disabled={formik.isSubmitting}>
                {formik.isSubmitting
                  ? <><Spinner animation="border" size="sm" className="me-2" />Saving…</>
                  : <>{editingDriver ? <><FaEdit className="me-2" />Save Changes</> : <><FaPlus className="me-2" />Add Driver</>}</>}
              </button>
            </div>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Delete Modal */}
      <Modal show={deleteModal} onHide={() => setDeleteModal(false)} centered size="sm">
        <Modal.Header closeButton className="dm-modal-header">
          <Modal.Title className="dm-modal-title">Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body className="dm-delete-body">
          <div className="dm-delete-icon"><FaTrash /></div>
          <p className="dm-delete-msg">
            Are you sure you want to delete driver <strong>{driverToDelete?.name}</strong>? This action cannot be undone.
          </p>
          <p className="dm-delete-warn">All associated records will be permanently removed.</p>
          <div className="dm-delete-actions">
            <button className="dm-btn-cancel" onClick={() => setDeleteModal(false)}>Cancel</button>
            <button className="dm-btn-delete" onClick={handleDelete}>Delete</button>
          </div>
        </Modal.Body>
      </Modal>

    </div>
  )
}
